import { cache } from "react";
import { createClient } from "../supabase/client";
import type {
	BlogPost,
	BlogPostsSubscription,
	EnrichedBlogPost,
	Profile,
} from "../supabase/types";
import { isValidUUID } from "../utils";

export async function getSearchResults(query: string) {
	const { data, error } = await createClient()
		.from("blog_posts")
		.select("id, title, slug")
		.ilike("title", `%${query}%`) // case-insensitive search
		.limit(10);

	if (error) {
		console.error(error);
		return [];
	}

	return data ?? [];
}

export const getPosts = cache(async () => {
	const { data: posts, error } = await createClient()
		.from("blog_posts")
		.select("*")
		.order("created_at", { ascending: false })
		.eq("published", true)
		.overrideTypes<BlogPost[]>();

	return {
		posts,
		error,
	};
});

export async function getPostSlugs() {
	const { data: postSlugs, error } = await createClient()
		.from("blog_posts")
		.select("slug, created_at, updated_at")
		.eq("published", true);

	return {
		postSlugs,
		error,
	};
}

export async function getTags() {
	const { data: tags, error } = await createClient()
		.from("tags")
		.select("*")
		.order("name");

	return {
		tags,
		error,
	};
}

export const getPost = cache(async (slugOrId: string) => {
	const query = createClient()
		.from("blog_posts")
		.select(
			`
				*,
				author:profiles(*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*)),
				city:cities(*),
				metadata:blog_post_metadata(*),
				reactions:blog_post_reactions(*, reaction:reaction_types(*)),
				comments:blog_post_comments(*, user:profiles(*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*))),
				bookmarks:blog_post_bookmarks(*, user:profiles(*, city:cities(*), user_type:user_types(*), account_status:account_status_types(*))),
				tags:blog_post_tags(*, tag:tags(*))
			`,
			{ count: "exact" },
		)
		.eq("published", true);

	if (isValidUUID(slugOrId)) {
		query.eq("id", slugOrId);
	} else {
		query.eq("slug", slugOrId);
	}

	const { data: post, error } = await query
		.maybeSingle()
		.overrideTypes<EnrichedBlogPost>();

	if (error) {
		console.log("Error loading blog post: ", error);
		return {
			post: null,
			error,
		};
	}

	return {
		post: post as EnrichedBlogPost,
		error,
	};
});

export const getBlogPost = cache(
	async (
		id: string,
	): Promise<{ post: BlogPost | null; error: Error | null }> => {
		const { data: post, error } = await createClient()
			.from("blog_posts")
			.select("*")
			.eq("id", id)
			.maybeSingle()
			.overrideTypes<BlogPost>();
		return {
			post: post as BlogPost,
			error,
		};
	},
);

export interface TopUserStats {
	user: Profile | null;
	stats: {
		posts_count: number;
		comments_count: number;
		reactions_count: number;
		bookmarks_count: number;
		total_engagement: number;
	};
}

export async function getTopTags(limit = 10) {
	const supabase = createClient();

	// First get tag counts using RPC
	const { data: tagCounts, error: countError } = await supabase.rpc(
		"get_top_blog_tags",
		{ p_limit: limit },
	);

	return {
		tags: tagCounts,
		error: countError,
	};
}

export async function getBlogPostsSubscriptions(email: string) {
	const supabase = createClient();
	const { data, error } = await supabase
		.from("blog_posts_subscriptions")
		.select("*")
		.eq("email", email)
		.single()
		.overrideTypes<BlogPostsSubscription>();
	return { data, error };
}

export async function createBlogPostsSubscription(
	email: string,
	source: string,
) {
	const supabase = createClient();

	// Create subscription without user_id for now
	// The database trigger will automatically link it to a user profile
	// when they register with the same email later
	const { data: subscription, error } = await supabase
		.from("blog_posts_subscriptions")
		.insert({
			email,
			source,
		})
		.single()
		.overrideTypes<BlogPostsSubscription>();

	return { data: subscription, error };
}
